import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

function addPeriodLocal(startYmd, period) {
  if (!startYmd) return '';
  const [y, m, d] = startYmd.split('-').map(Number);
  const dt = new Date(y, (m - 1), d); // local, sem UTC shift
  const end = new Date(dt);

  switch (period) {
    case 'daily': end.setDate(end.getDate() + 1); break;
    case 'weekly': end.setDate(end.getDate() + 7); break;
    case 'monthly': end.setMonth(end.getMonth() + 1); break;
    case 'quarterly': end.setMonth(end.getMonth() + 3); break;
    case 'annual': end.setFullYear(end.getFullYear() + 1); break;
    default: end.setMonth(end.getMonth() + 1);
  }

  const yy = end.getFullYear();
  const mm = String(end.getMonth() + 1).padStart(2, '0');
  const dd = String(end.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

const GoalForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    operation_id: '',
    unit_id: '',
    department_id: '',
    period: 'monthly',
    start_date: '',
    target_value: '',
    current_value: '0',
    unit_measure: '',
    is_positive_goal: true,
    repeat_type: 'none',
    status: 'active',
  });

  const [operations, setOperations] = useState([]);
  const [units, setUnits] = useState([]);
  const [departments, setDepartments] = useState([]);

  const [goalRaw, setGoalRaw] = useState(null);

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState(null);

  const [collapsed, setCollapsed] = useState({
    basic: false,
    period: false,
    values: false,
    repeat: false,
  });

  const toggleSection = (key) => {
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const loadInitialData = useCallback(async () => {
    try {
      setLoadingData(true);

      const [dashboardResponse, departmentsResponse] = await Promise.all([
        axios.get('/dashboard'),
        axios.get('/departments'),
      ]);

      if (dashboardResponse.data.success) {
        setOperations(dashboardResponse.data.data.operations || []);
        setUnits(dashboardResponse.data.data.units || []);
      }

      if (departmentsResponse.data.success) {
        setDepartments(departmentsResponse.data.data || []);
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar dados iniciais' });
    } finally {
      setLoadingData(false);
    }
  }, []);

  const loadGoal = useCallback(async () => {
    try {
      setLoadingData(true);
      const response = await axios.get(`/goals/${id}`);
      if (response.data.success) {
        setGoalRaw(response.data.goal);
      }
    } catch (error) {
      console.error('Error loading goal:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar meta' });
    } finally {
      setLoadingData(false);
    }
  }, [id]);

  useEffect(() => {
    loadInitialData();
    if (isEdit) loadGoal();
  }, [isEdit, loadInitialData, loadGoal]);

  // Quando dados + goalRaw estiverem disponíveis, mapeia para IDs (robusto)
  useEffect(() => {
    if (!isEdit || !goalRaw) return;
    if (!operations.length || !units.length || !departments.length) return;

    const op =
      operations.find((o) => String(o.id) === String(goalRaw.operation_id)) ||
      operations.find((o) => o.code && goalRaw.operation_code && o.code === goalRaw.operation_code) ||
      operations.find((o) => o.name && goalRaw.operation_name && o.name === goalRaw.operation_name);

    const unit =
      units.find((u) => String(u.id) === String(goalRaw.unit_id)) ||
      units.find((u) => u.code && goalRaw.unit_code && u.code === goalRaw.unit_code) ||
      units.find((u) => u.name && goalRaw.unit_name && u.name === goalRaw.unit_name);

    const dept =
      departments.find((d) => String(d.id) === String(goalRaw.department_id)) ||
      departments.find((d) => d.name && goalRaw.department_name && d.name === goalRaw.department_name);

    setFormData((prev) => ({
      ...prev,
      title: goalRaw.title || '',
      description: goalRaw.description || '',
      operation_id: op ? String(op.id) : '',
      unit_id: unit ? String(unit.id) : '',
      department_id: dept ? String(dept.id) : '',
      period: goalRaw.period || 'monthly',
      start_date: goalRaw.start_date ? String(goalRaw.start_date).split('T')[0] : '',
      target_value: goalRaw.target_value ?? '',
      current_value: goalRaw.current_value ?? '0',
      unit_measure: goalRaw.unit || '',
      is_positive_goal: goalRaw.is_positive_goal !== false,
      repeat_type: goalRaw.repeat_type || 'none',
      status: goalRaw.status || 'active',
    }));
  }, [isEdit, goalRaw, operations, units, departments]);

  // Helpers para limpar erros
  const clearError = (name) => {
    if (!errors[name]) return;
    setErrors((prev) => {
      const copy = { ...prev };
      delete copy[name];
      return copy;
    });
  };

  const onOperationChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      operation_id: value,
      unit_id: '',
      department_id: '',
    }));
    clearError('operation_id');
    clearError('unit_id');
    clearError('department_id');
  };

  const onUnitChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      unit_id: value,
      department_id: '',
    }));
    clearError('unit_id');
    clearError('department_id');
  };

  const onDepartmentChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, department_id: value }));
    clearError('department_id');
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    clearError(name);
  };

  // Filtrar unidades (se houver relação disponível)
  const filteredUnits = useMemo(() => {
    if (!formData.operation_id) return units;

    const opId = String(formData.operation_id);

    const canFilter = units.some((u) =>
      u.operation_id != null || u.operation?.id != null || u.operationId != null
    );
    if (!canFilter) return units;

    const result = units.filter((u) => {
      const a = u.operation_id != null ? String(u.operation_id) : null;
      const b = u.operation?.id != null ? String(u.operation.id) : null;
      const c = u.operationId != null ? String(u.operationId) : null;
      return a === opId || b === opId || c === opId;
    });

    // se não achou nada, não bloqueia o usuário
    return result.length ? result : units;
  }, [units, formData.operation_id]);

  // Filtrar departamentos (op + unit + ativos)
  const filteredDepartments = useMemo(() => {
    if (!formData.operation_id || !formData.unit_id) return [];

    const opId = String(formData.operation_id);
    const unitId = String(formData.unit_id);

    const canFilter = departments.some((d) =>
      d.operation?.id != null || d.operation_id != null || d.unit?.id != null || d.unit_id != null
    );

    const activeOnly = departments.filter((d) => d.is_active !== false);

    if (!canFilter) return activeOnly;

    const result = activeOnly.filter((d) => {
      const dop = d.operation?.id != null ? String(d.operation.id) : (d.operation_id != null ? String(d.operation_id) : null);
      const du = d.unit?.id != null ? String(d.unit.id) : (d.unit_id != null ? String(d.unit_id) : null);
      return dop === opId && du === unitId;
    });

    return result;
  }, [departments, formData.operation_id, formData.unit_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});
    setMessage(null);

    try {
      const op = operations.find((o) => String(o.id) === String(formData.operation_id));
      const unit = units.find((u) => String(u.id) === String(formData.unit_id));
      const dept = departments.find((d) => String(d.id) === String(formData.department_id));

      const newErrors = {};
      if (!op) newErrors.operation_id = 'Operação é obrigatória';
      if (!unit) newErrors.unit_id = 'Unidade é obrigatória';
      if (!dept) newErrors.department_id = 'Departamento é obrigatório';

      if (!formData.title?.trim()) newErrors.title = 'Título é obrigatório';
      if (!formData.start_date) newErrors.start_date = 'Data de início é obrigatória';
      if (!formData.target_value) newErrors.target_value = 'Valor alvo é obrigatório';

      if (Object.keys(newErrors).length) {
        setErrors(newErrors);
        setLoading(false);
        return;
      }

      const end_date = addPeriodLocal(formData.start_date, formData.period);

      const payload = {
        title: formData.title,
        description: formData.description || '',
        target_value: parseFloat(formData.target_value) || 0,
        current_value: parseFloat(formData.current_value) || 0,
        period: formData.period,
        start_date: formData.start_date,
        end_date,
        unit: formData.unit_measure || '',
        is_positive_goal: formData.is_positive_goal,
        repeat_type: formData.repeat_type || 'none',
        status: formData.status || 'active',

        // IDs (principal)
        operation_id: op.id,
        unit_id: unit.id,
        department_id: dept.id,

        // Extras (compatibilidade / logs / backend legado)
        operation_code: op.code,
        operation_name: op.name,
        unit_code: unit.code,
        unit_name: unit.name,
        department_name: dept.name,
      };

      if (isEdit) {
        await axios.put(`/goals/${id}`, payload);
        setMessage({ type: 'success', text: 'Meta atualizada com sucesso!' });
      } else {
        await axios.post('/goals', payload);
        setMessage({ type: 'success', text: 'Meta criada com sucesso!' });
      }

      setTimeout(() => navigate('/goals'), 1200);
    } catch (error) {
      console.error('Error saving goal:', error);
      if (error.response?.data?.errors) setErrors(error.response.data.errors);
      else {
        setMessage({
          type: 'error',
          text: error.response?.data?.error || 'Erro ao salvar meta. Por favor, tente novamente.',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="goal-form-page">
        <div className="loading"><span>Carregando...</span></div>
      </div>
    );
  }

  return (
    <div className="goal-form-page">
      <div className="form-container">
        <div className="form-header">
          <h4>{isEdit ? 'Editar Meta' : 'Nova Meta'}</h4>
        </div>

        {message && (
          <div className={`alert ${message.type === 'error' ? 'alert-danger' : 'alert-success'}`} role="alert">
            {message.text}
          </div>
        )}

        <div className="form-card">
          <form onSubmit={handleSubmit} className="needs-validation" noValidate>

            {/* Informações Básicas */}
            <div className="form-section">
              <div
                className={`form-section-header ${collapsed.basic ? 'collapsed' : ''}`}
                onClick={() => toggleSection('basic')}
                role="button"
                tabIndex={0}
              >
                <h5 className="form-section-title">
                  <i className="fas fa-info-circle"></i>
                  Informações Básicas
                </h5>
                <i className="fas fa-chevron-down collapse-icon"></i>
              </div>

              <div className={`form-section-content ${collapsed.basic ? 'collapsed' : ''}`}>
                <div className="form-grid form-grid-2">
                  <div className="field-group">
                    <div className="field-group-title">
                      <i className="fas fa-building"></i>
                      Operação e Unidade
                    </div>

                    <div className="row g-3">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label htmlFor="operation_id" className="form-label">
                            Operação <span className="text-danger">*</span>
                          </label>
                          <select
                            id="operation_id"
                            name="operation_id"
                            className={`form-select ${errors.operation_id ? 'is-invalid' : ''}`}
                            value={formData.operation_id}
                            onChange={onOperationChange}
                            required
                            disabled={isEdit}
                          >
                            <option value="">Selecione uma operação</option>
                            {operations.map((op) => (
                              <option key={op.id} value={op.id}>{op.name}</option>
                            ))}
                          </select>
                          {errors.operation_id && <div className="invalid-feedback d-block">{errors.operation_id}</div>}
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="form-group">
                          <label htmlFor="unit_id" className="form-label">
                            Unidade <span className="text-danger">*</span>
                          </label>
                          <select
                            id="unit_id"
                            name="unit_id"
                            className={`form-select ${errors.unit_id ? 'is-invalid' : ''}`}
                            value={formData.unit_id}
                            onChange={onUnitChange}
                            required
                            disabled={isEdit}
                          >
                            <option value="">Selecione uma unidade</option>
                            {filteredUnits.map((u) => (
                              <option key={u.id} value={u.id}>{u.name}</option>
                            ))}
                          </select>
                          {errors.unit_id && <div className="invalid-feedback d-block">{errors.unit_id}</div>}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="field-group">
                    <div className="field-group-title">
                      <i className="fas fa-sitemap"></i>
                      Departamento
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          navigate('/departments/create');
                        }}
                        className="btn btn-sm btn-outline-primary ms-2"
                        title="Criar novo departamento"
                      >
                        <i className="fas fa-plus"></i>
                      </button>
                    </div>

                    <div className="form-group">
                      <label htmlFor="department_id" className="form-label">
                        Departamento <span className="text-danger">*</span>
                      </label>
                      <select
                        id="department_id"
                        name="department_id"
                        className={`form-select ${errors.department_id ? 'is-invalid' : ''}`}
                        value={formData.department_id}
                        onChange={onDepartmentChange}
                        required
                        disabled={!formData.operation_id || !formData.unit_id}
                      >
                        <option value="">
                          {!formData.operation_id || !formData.unit_id
                            ? 'Selecione primeiro a operação e unidade'
                            : filteredDepartments.length === 0
                              ? 'Nenhum departamento disponível'
                              : 'Selecione um departamento'}
                        </option>

                        {filteredDepartments.map((dept) => (
                          <option key={dept.id} value={dept.id}>{dept.name}</option>
                        ))}
                      </select>

                      {errors.department_id && <div className="invalid-feedback d-block">{errors.department_id}</div>}

                      {!formData.operation_id || !formData.unit_id ? (
                        <div className="form-text text-muted">
                          Selecione primeiro a operação e unidade para ver os departamentos disponíveis
                        </div>
                      ) : filteredDepartments.length === 0 ? (
                        <div className="form-text text-warning">
                          Nenhum departamento encontrado para esta operação/unidade.&nbsp;
                          <a href="/departments/create" className="ms-1">Criar departamento</a>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div className="form-grid form-grid-2">
                  <div className="field-group">
                    <div className="field-group-title">
                      <i className="fas fa-heading"></i>
                      Título e Descrição
                    </div>

                    <div className="row g-3">
                      <div className="col-12">
                        <div className="form-group">
                          <label htmlFor="title" className="form-label">
                            Título <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            id="title"
                            name="title"
                            className={`form-control ${errors.title ? 'is-invalid' : ''}`}
                            value={formData.title}
                            onChange={handleChange}
                            required
                            placeholder="Digite o título da meta"
                          />
                          {errors.title && <div className="invalid-feedback d-block">{errors.title}</div>}
                        </div>
                      </div>

                      <div className="col-12">
                        <div className="form-group">
                          <label htmlFor="description" className="form-label">Descrição</label>
                          <textarea
                            id="description"
                            name="description"
                            className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                            value={formData.description}
                            onChange={handleChange}
                            rows="3"
                            placeholder="Digite a descrição da meta (opcional)"
                          />
                          {errors.description && <div className="invalid-feedback d-block">{errors.description}</div>}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Período */}
            <div className="form-section">
              <div
                className={`form-section-header ${collapsed.period ? 'collapsed' : ''}`}
                onClick={() => toggleSection('period')}
                role="button"
                tabIndex={0}
              >
                <h5 className="form-section-title">
                  <i className="fas fa-calendar-alt"></i>
                  Período
                </h5>
                <i className="fas fa-chevron-down collapse-icon"></i>
              </div>

              <div className={`form-section-content ${collapsed.period ? 'collapsed' : ''}`}>
                <div className="form-grid form-grid-2">
                  <div className="field-group">
                    <div className="field-group-title">
                      <i className="fas fa-clock"></i>
                      Período
                    </div>
                    <div className="form-group">
                      <select
                        id="period"
                        name="period"
                        className={`form-select ${errors.period ? 'is-invalid' : ''}`}
                        value={formData.period}
                        onChange={handleChange}
                        required
                        disabled={isEdit}
                      >
                        <option value="daily">Diário</option>
                        <option value="weekly">Semanal</option>
                        <option value="monthly">Mensal</option>
                        <option value="quarterly">Trimestral</option>
                        <option value="annual">Anual</option>
                      </select>
                      {errors.period && <div className="invalid-feedback d-block">{errors.period}</div>}
                    </div>
                  </div>

                  <div className="field-group">
                    <div className="field-group-title">
                      <i className="fas fa-calendar-day"></i>
                      Data de Início
                    </div>
                    <div className="form-group">
                      <input
                        type="date"
                        id="start_date"
                        name="start_date"
                        className={`form-control ${errors.start_date ? 'is-invalid' : ''}`}
                        value={formData.start_date}
                        onChange={handleChange}
                        required
                        disabled={isEdit}
                      />
                      {errors.start_date && <div className="invalid-feedback d-block">{errors.start_date}</div>}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Valores */}
            <div className="form-section">
              <div
                className={`form-section-header ${collapsed.values ? 'collapsed' : ''}`}
                onClick={() => toggleSection('values')}
                role="button"
                tabIndex={0}
              >
                <h5 className="form-section-title">
                  <i className="fas fa-chart-line"></i>
                  Valores
                </h5>
                <i className="fas fa-chevron-down collapse-icon"></i>
              </div>

              <div className={`form-section-content ${collapsed.values ? 'collapsed' : ''}`}>
                <div className="form-grid form-grid-2">
                  <div className="field-group">
                    <div className="field-group-title">
                      <i className="fas fa-bullseye"></i>
                      Valor Alvo
                    </div>
                    <div className="form-group">
                      <input
                        type="number"
                        id="target_value"
                        name="target_value"
                        className={`form-control ${errors.target_value ? 'is-invalid' : ''}`}
                        value={formData.target_value}
                        onChange={handleChange}
                        required
                        min="0.01"
                        step="0.01"
                        placeholder="0.00"
                        disabled={isEdit}
                      />
                      {errors.target_value && <div className="invalid-feedback d-block">{errors.target_value}</div>}
                    </div>
                  </div>

                  <div className="field-group">
                    <div className="field-group-title">
                      <i className="fas fa-chart-bar"></i>
                      Valor Atual
                    </div>
                    <div className="form-group">
                      <input
                        type="number"
                        id="current_value"
                        name="current_value"
                        className={`form-control ${errors.current_value ? 'is-invalid' : ''}`}
                        value={formData.current_value}
                        onChange={handleChange}
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        disabled={isEdit}
                      />
                      {errors.current_value && <div className="invalid-feedback d-block">{errors.current_value}</div>}
                    </div>
                  </div>
                </div>

                <div className="form-grid form-grid-2">
                  <div className="field-group">
                    <div className="field-group-title">
                      <i className="fas fa-ruler"></i>
                      Unidade de Medida
                    </div>
                    <div className="form-group">
                      <input
                        type="text"
                        id="unit_measure"
                        name="unit_measure"
                        className={`form-control ${errors.unit_measure ? 'is-invalid' : ''}`}
                        value={formData.unit_measure}
                        onChange={handleChange}
                        placeholder="Ex: horas, páginas, quilômetros"
                        disabled={isEdit}
                      />
                      {errors.unit_measure && <div className="invalid-feedback d-block">{errors.unit_measure}</div>}
                      <div className="form-text">Unidade de medida (ex: horas, páginas, quilômetros)</div>
                    </div>
                  </div>

                  <div className="field-group goal-type-field">
                    <div className="field-group-title">
                      <i className="fas fa-arrow-up"></i>
                      Tipo de Meta
                    </div>

                    <div className="goal-type-toggle">
                      <div className={`form-check goal-type-option ${formData.is_positive_goal ? 'selected' : ''}`} data-type="positive">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="is_positive_goal"
                          id="goal_positive"
                          checked={formData.is_positive_goal}
                          onChange={() => setFormData((p) => ({ ...p, is_positive_goal: true }))}
                        />
                        <label className="form-check-label" htmlFor="goal_positive">
                          <i className="fas fa-arrow-up text-success"></i>
                          <strong>Meta Positiva</strong>
                          <small className="d-block text-muted">Valores maiores são melhores</small>
                          <small className="d-block text-muted">Ex: vendas, produtividade, satisfação</small>
                        </label>
                      </div>

                      <div className={`form-check goal-type-option ${!formData.is_positive_goal ? 'selected' : ''}`} data-type="negative">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="is_positive_goal"
                          id="goal_negative"
                          checked={!formData.is_positive_goal}
                          onChange={() => setFormData((p) => ({ ...p, is_positive_goal: false }))}
                        />
                        <label className="form-check-label" htmlFor="goal_negative">
                          <i className="fas fa-arrow-down text-danger"></i>
                          <strong>Meta Negativa</strong>
                          <small className="d-block text-muted">Valores menores são melhores</small>
                          <small className="d-block text-muted">Ex: despesas, taxa de churn, erros</small>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tipo de Repetição */}
            <div className="form-section">
              <div
                className={`form-section-header ${collapsed.repeat ? 'collapsed' : ''}`}
                onClick={() => toggleSection('repeat')}
                role="button"
                tabIndex={0}
              >
                <h5 className="form-section-title">
                  <i className="fas fa-sync-alt"></i>
                  Tipo de Repetição
                </h5>
                <i className="fas fa-chevron-down collapse-icon"></i>
              </div>

              <div className={`form-section-content ${collapsed.repeat ? 'collapsed' : ''}`}>
                <div className="field-group">
                  <div className="field-group-title">
                    <i className="fas fa-repeat"></i>
                    Configuração de Repetição
                  </div>

                  <div className="form-group">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="repeat_type"
                        id="repeat_none"
                        value="none"
                        checked={formData.repeat_type === 'none'}
                        onChange={handleChange}
                      />
                      <label className="form-check-label" htmlFor="repeat_none">Sem Repetição</label>
                    </div>

                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="repeat_type"
                        id="repeat_auto"
                        value="auto"
                        checked={formData.repeat_type === 'auto'}
                        onChange={handleChange}
                      />
                      <label className="form-check-label" htmlFor="repeat_auto">Repetição Automática</label>
                    </div>

                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="repeat_type"
                        id="repeat_manual"
                        value="manual"
                        checked={formData.repeat_type === 'manual'}
                        onChange={handleChange}
                      />
                      <label className="form-check-label" htmlFor="repeat_manual">Repetição Manual</label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="form-actions">
              <Link to="/goals" className="btn btn-secondary">
                <i className="fas fa-arrow-left"></i>
                Voltar
              </Link>

              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Salvando...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save"></i>
                    {isEdit ? 'Atualizar Meta' : 'Criar Meta'}
                  </>
                )}
              </button>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
};

export default GoalForm;
